import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tabs,
  Typography
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Done as DoneIcon,
  EditNote as EditNoteIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  VolumeUp as VolumeUpIcon
} from '@mui/icons-material';
import AcessoRestrito from '../../components/AcessoRestrito';
import api from '../../services/api';

const STATUS_COR = {
  Agendado: 'default',
  Aguardando: 'warning',
  Chamando: 'info',
  'Em atendimento': 'primary',
  Atendido: 'success',
  Desistente: 'default',
  Cancelado: 'error'
};

const STATUS_FINALIZADOS = new Set(['Atendido', 'Desistente', 'Cancelado']);

function formatarDataHora(valor) {
  if (!valor) return 'Não informado';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return 'Não informado';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short'
  }).format(data);
}

function mensagemErro(error, padrao) {
  return error?.response?.data?.error || error?.response?.data?.message || padrao;
}

export default function FormularioAtendimento() {
  const [atendimentos, setAtendimentos] = useState([]);
  const [colaborador, setColaborador] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [atendimentoSelecionado, setAtendimentoSelecionado] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [anotacao, setAnotacao] = useState({ data: new Date().toISOString().slice(0, 10), tipo: 'Primeira Consulta', texto: '' });
  const [dialogoDocumentoAberto, setDialogoDocumentoAberto] = useState(false);
  const [documento, setDocumento] = useState({ tipo: 'Receita', titulo: '', texto: '' });
  const [abaFormulario, setAbaFormulario] = useState('atendimento');

  const carregarAtendimentos = useCallback(async () => {
    setCarregando(true);
    try {
      const resposta = await api.get('/meus-atendimentos');
      setAtendimentos(Array.isArray(resposta.data?.atendimentos) ? resposta.data.atendimentos : []);
      setColaborador(resposta.data?.colaborador || null);
    } catch (error) {
      setErro(mensagemErro(error, 'Não foi possível carregar seus atendimentos.'));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregarAtendimentos();
  }, [carregarAtendimentos]);

  const abrirFormulario = async (atendimento) => {
    setAtendimentoSelecionado(atendimento);
    setAbaFormulario('atendimento');
    setDetalhe(null);
    setAnotacao({ data: new Date().toISOString().slice(0, 10), tipo: 'Primeira Consulta', texto: '' });
    setCarregandoDetalhe(true);
    try {
      const resposta = await api.get(`/meus-atendimentos/${atendimento.id_atendimento}`);
      setDetalhe(resposta.data);
    } catch (error) {
      setErro(mensagemErro(error, 'Não foi possível carregar o formulário de atendimento.'));
      setAtendimentoSelecionado(null);
    } finally {
      setCarregandoDetalhe(false);
    }
  };

  const fecharFormulario = () => {
    if (salvando) return;
    setAtendimentoSelecionado(null);
    setDetalhe(null);
    setDialogoDocumentoAberto(false);
  };

  const abrirDocumento = () => {
    setDocumento({ tipo: 'Receita', titulo: '', texto: '' });
    setDialogoDocumentoAberto(true);
  };

  const imprimirDocumento = async () => {
    if (!atendimentoAtual || !documento.texto.trim()) {
      setErro('Informe o conteúdo antes de imprimir o documento.');
      return;
    }

    const tituloDocumento = documento.tipo === 'Outros' ? documento.titulo.trim() : documento.tipo;
    if (!tituloDocumento) {
      setErro('Informe o título do documento.');
      return;
    }

    // Deve ser aberta no clique para que o navegador não bloqueie a impressão após a requisição.
    const janela = window.open('', '_blank');
    if (!janela) {
      setErro('Não foi possível abrir a impressão. Verifique se o navegador bloqueou pop-ups.');
      return;
    }

    setSalvando(true);
    try {
      const resposta = await api.post(`/meus-atendimentos/${atendimentoSelecionado.id_atendimento}/anotacoes`, {
        data: new Date().toISOString().slice(0, 10),
        tipo: `Documento emitido — ${tituloDocumento}`,
        texto: documento.texto.trim()
      });
      const anotacaoRegistrada = resposta.data?.anotacao;
      if (anotacaoRegistrada) {
        setDetalhe((valor) => valor ? {
          ...valor,
          anotacoes: [anotacaoRegistrada, ...(valor.anotacoes || [])]
        } : valor);
      }

      const pdf = await api.post(`/meus-atendimentos/${atendimentoSelecionado.id_atendimento}/documentos/pdf`, {
        tipo: documento.tipo,
        titulo: documento.titulo.trim(),
        texto: documento.texto.trim()
      }, { responseType: 'blob' });
      const urlPdf = URL.createObjectURL(pdf.data);
      janela.location.replace(urlPdf);
      window.setTimeout(() => URL.revokeObjectURL(urlPdf), 60000);
    } catch (error) {
      janela.close();
      setErro(mensagemErro(error, 'Não foi possível gerar o documento em PDF.'));
      return;
    } finally {
      setSalvando(false);
    }

    setDialogoDocumentoAberto(false);
    setSucesso('Documento registrado no histórico e aberto em PDF para impressão.');
  };

  const atualizarStatus = async (status, rechamada = false) => {
    if (!atendimentoSelecionado) return;
    setSalvando(true);
    try {
      await api.patch(`/meus-atendimentos/${atendimentoSelecionado.id_atendimento}`, { status });
      setSucesso(rechamada ? 'Paciente chamado novamente.' : `Atendimento atualizado para “${status}”.`);
      await carregarAtendimentos();
      const resposta = await api.get(`/meus-atendimentos/${atendimentoSelecionado.id_atendimento}`);
      setDetalhe(resposta.data);
      setAtendimentoSelecionado(resposta.data.atendimento);
    } catch (error) {
      setErro(mensagemErro(error, 'Não foi possível atualizar o atendimento.'));
    } finally {
      setSalvando(false);
    }
  };

  const salvarAnotacao = async () => {
    if (!atendimentoSelecionado || !anotacao.texto.trim()) {
      setErro('Informe o texto da anotação.');
      return;
    }
    setSalvando(true);
    try {
      await api.post(`/meus-atendimentos/${atendimentoSelecionado.id_atendimento}/anotacoes`, anotacao);
      setAnotacao((valor) => ({ ...valor, texto: '' }));
      const resposta = await api.get(`/meus-atendimentos/${atendimentoSelecionado.id_atendimento}`);
      setDetalhe(resposta.data);
      setSucesso('Anotação salva com sucesso.');
    } catch (error) {
      setErro(mensagemErro(error, 'Não foi possível salvar a anotação.'));
    } finally {
      setSalvando(false);
    }
  };

  const atendimentoAtual = detalhe?.atendimento || atendimentoSelecionado;
  const finalizado = STATUS_FINALIZADOS.has(atendimentoAtual?.status);

  return (
    <AcessoRestrito modulo="formularioatendimento">
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssignmentIcon color="primary" fontSize="large" /> Formulário de Atendimento
            </Typography>
            <Typography color="text.secondary">
              {colaborador ? `Atendimentos de ${colaborador.nome}` : 'Seus atendimentos'} — do mais recente ao mais antigo.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={carregarAtendimentos} disabled={carregando}>
            Atualizar
          </Button>
        </Stack>

        <Paper elevation={2}>
          {carregando ? (
            <Box sx={{ minHeight: 220, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><CircularProgress /></Box>
          ) : (
            <TableContainer>
              <Table aria-label="Lista de meus atendimentos">
                <TableHead>
                  <TableRow>
                    <TableCell>Data/hora</TableCell>
                    <TableCell>Senha</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Serviço</TableCell>
                    <TableCell>Local</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {atendimentos.map((atendimento) => (
                    <TableRow key={atendimento.id_atendimento} hover>
                      <TableCell>{formatarDataHora(atendimento.dataHoraInicio)}</TableCell>
                      <TableCell>{atendimento.ordem_chegada || '—'}</TableCell>
                      <TableCell>{atendimento.clienteNome}</TableCell>
                      <TableCell>{atendimento.servicoNome}</TableCell>
                      <TableCell>{atendimento.localNome}</TableCell>
                      <TableCell><Chip size="small" label={atendimento.status} color={STATUS_COR[atendimento.status] || 'default'} /></TableCell>
                      <TableCell align="right">
                        <Button size="small" variant="contained" startIcon={<EditNoteIcon />} onClick={() => abrirFormulario(atendimento)}>
                          Abrir formulário
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!atendimentos.length && (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 5 }}>Nenhum atendimento encontrado para este colaborador.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      <Dialog
        open={Boolean(atendimentoSelecionado)}
        onClose={fecharFormulario}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: { width: '90vw', maxWidth: 'none', height: '90vh', maxHeight: '90vh' } }}
      >
        <DialogTitle sx={{ pb: 1.5 }}>Formulário de Atendimento</DialogTitle>
        <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', p: { xs: 2, md: 3 } }}>
          {carregandoDetalhe || !atendimentoAtual ? <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress /></Box> : (
            <Stack spacing={2} sx={{ minHeight: 0, height: '100%', flex: 1 }}>
              <Tabs value={abaFormulario} onChange={(_, valor) => setAbaFormulario(valor)} aria-label="Abas do formulário de atendimento">
                <Tab value="atendimento" label="Atendimento e anotação" />
                <Tab value="historico" label="Histórico completo do paciente" />
              </Tabs>
              {abaFormulario === 'atendimento' ? <>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(330px, 0.8fr) minmax(0, 1.6fr)' }, gap: 2.5, minHeight: 0, overflow: 'hidden', flex: 1 }}>
                <Stack spacing={2} sx={{ minWidth: 0, minHeight: 0, overflowY: 'auto', pr: { lg: 0.5 } }}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="h6">{atendimentoAtual.clienteNome}</Typography>
                    <Typography color="text.secondary">Senha {atendimentoAtual.ordem_chegada || '—'} · {atendimentoAtual.servicoNome} · {atendimentoAtual.localNome}</Typography>
                    <Typography color="text.secondary">{formatarDataHora(atendimentoAtual.dataHoraInicio)}</Typography>
                    <Box sx={{ mt: 1.25 }}><Chip label={atendimentoAtual.status} color={STATUS_COR[atendimentoAtual.status] || 'default'} /></Box>
                  </Paper>

                  {!finalizado && (
                    <Stack spacing={1} sx={{ '& > .MuiButton-root': { minHeight: 44, justifyContent: 'flex-start', px: 2 } }}>
                      {atendimentoAtual.status === 'Aguardando' && <Button variant="contained" startIcon={<VolumeUpIcon />} onClick={() => atualizarStatus('Chamando')} disabled={salvando}>Chamar paciente</Button>}
                      {['Chamando', 'Em atendimento'].includes(atendimentoAtual.status) && <Button variant="contained" color="success" startIcon={<DoneIcon />} onClick={() => atualizarStatus('Atendido')} disabled={salvando}>Finalizar atendimento</Button>}
                      {atendimentoAtual.status === 'Chamando' && <Button variant="outlined" startIcon={<VolumeUpIcon />} onClick={() => atualizarStatus('Chamando', true)} disabled={salvando}>Chamar novamente</Button>}
                      {atendimentoAtual.status === 'Chamando' && <Button variant="outlined" onClick={() => atualizarStatus('Em atendimento')} disabled={salvando}>Em atendimento</Button>}
                      <Button variant="outlined" startIcon={<PrintIcon />} onClick={abrirDocumento} disabled={salvando}>Emitir documento ao paciente</Button>
                      <Button variant="outlined" color="warning" onClick={() => atualizarStatus('Desistente')} disabled={salvando}>Desistente</Button>
                      <Button variant="outlined" color="error" onClick={() => atualizarStatus('Cancelado')} disabled={salvando}>Cancelar</Button>
                    </Stack>
                  )}
                </Stack>

                <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
                  <Typography variant="h6" sx={{ mb: 0.5 }}>Anotações</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Registre a evolução e as observações do atendimento.</Typography>
                  <TextField label="Anotação" value={anotacao.texto} onChange={(event) => setAnotacao((valor) => ({ ...valor, texto: event.target.value }))} multiline minRows={1} fullWidth disabled={salvando} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', '& .MuiInputBase-root': { flex: 1, minHeight: 0, alignItems: 'stretch' }, '& textarea': { height: '100% !important', overflowY: 'auto !important', lineHeight: 1.6 } }} />
                  <Button variant="contained" onClick={salvarAnotacao} disabled={salvando || !anotacao.texto.trim()} sx={{ mt: 2, minHeight: 46, alignSelf: 'flex-end', minWidth: { xs: '100%', sm: 220 } }}>Salvar anotação</Button>
                </Paper>
              </Box>
              </> : <Box sx={{ minHeight: 0, overflowY: 'auto', flex: 1, pr: 0.5 }}>
                <Typography variant="h6" sx={{ mb: 1.5 }}>Histórico completo do paciente</Typography>
                <Stack spacing={1.25}>
                  {detalhe?.anotacoes?.length ? detalhe.anotacoes.map((item) => (
                    <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Typography variant="subtitle2">{item.tipo} · {formatarDataHora(item.criadoEm || item.data)}</Typography>
                      <Typography sx={{ whiteSpace: 'pre-wrap' }}>{item.texto}</Typography>
                    </Paper>
                  )) : <Typography color="text.secondary">Nenhuma anotação registrada para este paciente.</Typography>}
                </Stack>
              </Box>}
            </Stack>
          )}
        </DialogContent>
        <DialogActions><Button onClick={fecharFormulario} disabled={salvando}>Fechar</Button></DialogActions>
      </Dialog>

      <Dialog open={dialogoDocumentoAberto} onClose={() => setDialogoDocumentoAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>Emitir documento</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography color="text.secondary">
              O documento será aberto para impressão com o cabeçalho da clínica e a assinatura do colaborador responsável.
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="tipo-documento">Tipo de documento</InputLabel>
              <Select
                labelId="tipo-documento"
                label="Tipo de documento"
                value={documento.tipo}
                onChange={(event) => setDocumento((valor) => ({ ...valor, tipo: event.target.value }))}
              >
                <MenuItem value="Receita">Receita</MenuItem>
                <MenuItem value="Encaminhamento">Encaminhamento</MenuItem>
                <MenuItem value="Atestado">Atestado</MenuItem>
                <MenuItem value="Declaração de comparecimento">Declaração de comparecimento</MenuItem>
                <MenuItem value="Solicitação de exame">Solicitação de exame</MenuItem>
                <MenuItem value="Relatório clínico/evolução">Relatório clínico/evolução</MenuItem>
                <MenuItem value="Orientações ao paciente">Orientações ao paciente</MenuItem>
                <MenuItem value="Outros">Outros, com título livre</MenuItem>
              </Select>
            </FormControl>
            {documento.tipo === 'Outros' && (
              <TextField
                label="Título do documento"
                value={documento.titulo}
                onChange={(event) => setDocumento((valor) => ({ ...valor, titulo: event.target.value }))}
                fullWidth
                required
              />
            )}
            <TextField
              label={documento.tipo === 'Receita'
                ? 'Itens prescritos e orientações'
                : documento.tipo === 'Encaminhamento'
                  ? 'Destino e justificativa do encaminhamento'
                  : 'Conteúdo do documento'}
              value={documento.texto}
              onChange={(event) => setDocumento((valor) => ({ ...valor, texto: event.target.value }))}
              multiline
              minRows={9}
              fullWidth
              autoFocus
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogoDocumentoAberto(false)}>Cancelar</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={imprimirDocumento} disabled={!documento.texto.trim() || (documento.tipo === 'Outros' && !documento.titulo.trim())}>
            Imprimir
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={Boolean(erro)} autoHideDuration={7000} onClose={() => setErro('')}><Alert severity="error" onClose={() => setErro('')} sx={{ width: '100%' }}>{erro}</Alert></Snackbar>
      <Snackbar open={Boolean(sucesso)} autoHideDuration={4000} onClose={() => setSucesso('')}><Alert severity="success" onClose={() => setSucesso('')} sx={{ width: '100%' }}>{sucesso}</Alert></Snackbar>
    </AcessoRestrito>
  );
}
